import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { UserEndpointQueryKeys } from '../../../../Shared/constants/user/endpoints';
import { UserRoles, isAdminRole } from '../../../../Shared/constants/userRoles';
import { ApiError } from '../../../../Shared/interfaces/ApiError';
import {
  GetUsersApiResponse,
  UserInterface,
} from '../../../../Shared/interfaces/user/UserInterfaces';
import useApiClient from '../../../../Shared/services/api';
import { getUsers } from '../../../../Shared/services/user/userService';
import { generateSearchSuggestions } from '../../../../Shared/utils/searchSuggestionUtils';
import {
  searchData,
  sortBySearchRelevance,
} from '../../../../Shared/utils/searchUtils';
import { PAGE_SIZES } from '../../../../UiComponentsLibrary/CustomPagination/constants/PageSizes';
import { SearchSuggestion } from '../../../../UiComponentsLibrary/CustomSearchInput';
import { ACTION_TYPES } from '../../../constants/actionsTypes';
import { USER_STATUS } from '../../../constants/userStatus';
import { SEARCHABLE_USER_FIELDS } from '../../../constants/userTableConstants';
import { CreateUserFromData } from '../../../interfaces/userInterfaces';
import { useDeleteUserMutation } from './useDeleteUserMutation';
import { useEditUserMutation } from './useEditUserMutation';
import { t } from 'i18next';
import { SIDE_NAVIGATION_RESET_PARAM, SIDE_NAVIGATION_RESET_PARAM_KEY } from '../../../../Shared/constants/commonConstants';
import { URLS } from '../../../../Shared/constants/url';

export const useUserManagement = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentSize, setCurrentSize] = useState<number>(PAGE_SIZES[0]);
  const [showStatusUsers, setShowStatusUsers] = useState<
    (typeof USER_STATUS)[keyof typeof USER_STATUS]
  >(USER_STATUS.ACTIVE);
  const [isViewUser, setIsViewUser] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>('');
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isEditUser, setIsEditUser] = useState<boolean>(false);
  const [isCreateUser, setIsCreateUser] = useState<boolean>(false);
  const [userFormData, setUserFormData] = useState<CreateUserFromData>({
    firstName: '',
    lastName: '',
    emailId: '',
    userRole: '',
    category: null,
    privilegedMode: false,
    orgName: null,
  });
  const [searchSuggestions, setSearchSuggestions] = useState<
    SearchSuggestion[]
  >([]);
  const [filteredUsersData, setFilteredUsersData] = useState<UserInterface[]>(
    []
  );
  const [searchParams] = useSearchParams();

  // Snackbar state for success messages
  const [customSnackbarMessage, setCustomSnackbarMessage] =
    useState<string>('');
  const navigate = useNavigate();

  const api = useApiClient();

  // Reset all form states when reset URL parameter is present
  useEffect(() => {
    if (searchParams.get(SIDE_NAVIGATION_RESET_PARAM_KEY) === SIDE_NAVIGATION_RESET_PARAM.reset) {
      setIsViewUser(false);
      setIsEditUser(false);
      setIsCreateUser(false);
      setDeleteModal(false);
      setUserId('');
      setUserFormData({
        firstName: '',
        lastName: '',
        emailId: '',
        userRole: '',
        category: null,
        privilegedMode: false,
        orgName: null,
      });

      // Clean URL by removing reset parameter without triggering navigation
      navigate(URLS.USERS, { replace: true });
    }
  }, [searchParams, navigate]);

  const {
    error,
    isLoading,
    isError,
    data: getUsersResponse,
  } = useQuery<GetUsersApiResponse, ApiError>({
    queryKey: [UserEndpointQueryKeys.GET_USERS],
    queryFn: () => getUsers(api),
    enabled: true,
  });

  const allUsersData = useMemo(() => {
    const users = getUsersResponse?.users || [];
    return users
      .filter((user) => user?.userStatus === showStatusUsers)
      .sort((a, b) => {
        if (a.userId > b.userId) return 1;
        if (a.userId < b.userId) return -1;
        return 0;
      });
  }, [getUsersResponse?.users, showStatusUsers]);

  useEffect(() => {
    if (allUsersData && allUsersData.length > 0) {
      const suggestions = generateSearchSuggestions(
        allUsersData,
        SEARCHABLE_USER_FIELDS
      );
      setSearchSuggestions(suggestions);
    }
  }, [allUsersData, showStatusUsers]);

  useEffect(() => {
    if (!allUsersData) {
      setFilteredUsersData([]);
      return;
    }
    if (!searchValue.trim()) {
      setFilteredUsersData(allUsersData);
      return;
    }
    const searchResults = searchData(
      allUsersData,
      searchValue,
      SEARCHABLE_USER_FIELDS
    );
    const sortedResults = sortBySearchRelevance(
      searchResults,
      searchValue,
      SEARCHABLE_USER_FIELDS
    );
    setFilteredUsersData(sortedResults);
  }, [allUsersData, searchValue, showStatusUsers]);

  useEffect(() => {
    setSearchValue('');
    setCurrentPage(0);
  }, [showStatusUsers]);

  const totalItems = filteredUsersData.length;
  const startIndex = currentPage * currentSize;
  const endIndex = startIndex + currentSize;
  const getUsersData = filteredUsersData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handlePageSizeChange = (size: number) => {
    setCurrentSize(size);
    setCurrentPage(0); // Reset to page 0 when changing page size
  };

  const handleSearchChange = (value: string) => {
    if (value !== searchValue) {
      setSearchValue(value);
      setCurrentPage(0); // Reset to page 0 when search changes
    }
  };

  const deleteUserMutation = useDeleteUserMutation(
    setDeleteModal,
    setCurrentPage,
    setCustomSnackbarMessage
  );

  const editUserMutation = useEditUserMutation(
    setIsEditUser,
    setCustomSnackbarMessage
  );

  // Data Table Functions
  const handleDataTableAction = (actionType: string, row: UserInterface) => {
    if (actionType === ACTION_TYPES.VIEW_USER) {
      setIsViewUser(true);
      setUserId(row.userId);
    }

    if (actionType === ACTION_TYPES.EDIT_USER) {
      editUserMutation.reset(); // Clear previous error state
      setIsEditUser(true);
      setUserId(row.userId);
      setUserFormData({
        firstName: row.firstName,
        lastName: row.lastName,
        emailId: row.emailId,
        userRole: row.userRole,
        category: row.category,
        privilegedMode: row.privilegedMode ?? false,
        orgName: row.orgName ?? null,
      });
    }

    if (actionType === ACTION_TYPES.DELETE_USER) {
      deleteUserMutation.reset(); // Clear previous error state
      setDeleteModal(true);
      setUserId(row.userId);
    }

    if (actionType === ACTION_TYPES.RESTORE_USER) {
      // Send ACTIVE status for the DELETED user, force privilegedMode for admins
      editUserMutation.mutate({
        userId: row.userId,
        userData: { ...row, userStatus: USER_STATUS.ACTIVE, privilegedMode: isAdminRole(row.userRole) ? true : row.privilegedMode ?? false },
        successMessage: t('user-restored-success-message'),
      });
    }
  };

  const toOpenEditUser = isEditUser && userId;
  const editUserProps = {
    userFormData,
    setUserFormData,
    editUserMutation,
    userId,
    setIsEditUser,
  };

  const mainContentProps = {
    isError,
    error,
    isLoading,
    getUsersData,
    handleDataTableAction,
    renderSearchAndPaginationProps: {
      handleSearchChange,
      searchValue,
      currentSize,
      handlePageChange,
      handlePageSizeChange,
      searchSuggestions,
    },
    renderPaginationProps: {
      currentPage,
      currentSize,
      totalItems,
      handlePageChange,
    },
  };

  return {
    editUserProps,
    viewUserProps: {
      userId,
      isViewUser,
      setIsViewUser,
    },
    toOpenEditUser,
    createUserProps: {
      isCreateUser,
      setIsCreateUser,
    },
    snackbarProps: {
      customSnackbarMessage,
      setCustomSnackbarMessage,
    },
    deleteModalProps: {
      deleteModal,
      setDeleteModal,
      userId,
      deleteUserMutation,
    },
    mainContentProps,
    userManagementCardActionProps: {
      showStatusUsers,
      handleStatusUsersView: setShowStatusUsers,
      setIsCreateUser,
    },
  };
};
